import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  IndianRupee,
  BellOff,
  Shuffle,
  MousePointerClick,
  ArrowRight,
} from "lucide-react";

const FEATURES = [
  {
    tag: "01 / CLARITY",
    icon: IndianRupee,
    title: "Risk in rupees.\nNot in Greeks.",
    body:
      "Set your max loss as ₹2,500. That's it. No delta, no theta, no vega. Tradecure translates every position into plain rupees so you know exactly what's on the line — before you click execute.",
    visual: "rupee",
  },
  {
    tag: "02 / DISCIPLINE",
    icon: BellOff,
    title: "No screen.\nNo emotion.\nNo interference.",
    body:
      "Once your strategy is live, Tradecure runs it end-to-end. Entries, exits, SL, target, trailing — all automatic. You don't override your plan at 2:47pm because a candle looked scary.",
    visual: "discipline",
  },
  {
    tag: "03 / POWER",
    icon: Shuffle,
    title: "Auto-transformative\nstrategies.",
    body:
      "Our flagship. If the market flips mid-trade, Tradecure re-shapes your legs in real time — the way institutional desks do. You don't need to be watching. The strategy adjusts itself.",
    visual: "adapt",
  },
  {
    tag: "04 / SPEED",
    icon: MousePointerClick,
    title: "Zero code.\nZero prompts.\nZero tutorials.",
    body:
      "If you can schedule a grocery order, you can schedule a trade. Pick a strategy, set your budget and SL, hit deploy. Done. No JSON, no Python, no broker API docs.",
    visual: "nocode",
  },
];

export default function FeaturesScroll() {
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // Horizontal translate: 4 cards, translate from 0% → -(n-1)/n * 100 % of the row
  // The row is wider than the viewport (each card is ~ viewport wide).
  // We slide by (FEATURES.length - 1) * card_width
  const n = FEATURES.length;
  const x = useTransform(scrollYProgress, [0, 1], ["0%", `-${(n - 1) * (100 / n)}%`]);

  return (
    <section
      id="features"
      data-testid="features-section"
      ref={containerRef}
      className="relative bg-[#f8fafc]"
      style={{ height: `${n * 100}vh` }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden flex flex-col">
        {/* Animated thread-wave background */}
        <ThreadWave progress={scrollYProgress} />

        {/* Top label bar */}
        <div className="pt-24 md:pt-28 pb-6 md:pb-8 relative z-10">
          <div className="mx-auto max-w-7xl px-5 md:px-8 flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="font-mono text-xs font-bold text-[#084392] tracking-[0.22em]">
                / WHAT YOU GET
              </div>
              <h2 className="font-heading mt-3 text-3xl md:text-4xl lg:text-5xl tracking-tighter leading-[1]">
                Four features.{" "}
                <span className="text-[#084392]">Zero noise.</span>
              </h2>
            </div>
            <Progress progress={scrollYProgress} total={n} />
          </div>
        </div>

        {/* Horizontal slide rail */}
        <div className="flex-1 flex items-center relative z-10">
          <motion.div
            style={{ x, width: `${n * 100}vw` }}
            className="flex h-full items-center will-change-transform"
          >
            {FEATURES.map((f, i) => (
              <FeatureSlide
                key={i}
                feature={f}
                index={i}
                total={n}
                progress={scrollYProgress}
              />
            ))}
          </motion.div>
        </div>

        {/* Scroll hint */}
        <div className="pb-6 flex justify-center relative z-10">
          <div className="flex items-center gap-2 text-xs font-mono text-[#475569]">
            <span>Scroll</span>
            <ArrowRight size={14} className="text-[#084392]" />
          </div>
        </div>
      </div>
    </section>
  );
}

function Progress({ progress, total }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: total }).map((_, i) => (
        <ProgressBar key={i} index={i} total={total} progress={progress} />
      ))}
    </div>
  );
}

function ProgressBar({ index, total, progress }) {
  const start = index / total;
  const end = (index + 1) / total;
  const fill = useTransform(progress, [start, end], ["0%", "100%"]);
  return (
    <div
      data-testid={`feature-progress-${index}`}
      className="h-1.5 w-14 rounded-full bg-[#e2e8f0] overflow-hidden"
    >
      <motion.div
        className="h-full bg-[#084392]"
        style={{ width: fill }}
      />
    </div>
  );
}

function FeatureSlide({ feature, index, total, progress }) {
  const Icon = feature.icon;

  const center = (index + 0.5) / total;
  const span = 1 / total;

  const scale = useTransform(
    progress,
    [center - span, center - span * 0.5, center, center + span * 0.5, center + span],
    [0.82, 0.92, 1, 0.92, 0.82]
  );
  const y = useTransform(
    progress,
    [center - span, center - span * 0.5, center, center + span * 0.5, center + span],
    [30, -10, 0, 14, 30]
  );
  const rotate = useTransform(
    progress,
    [center - span, center, center + span],
    [3, 0, -3]
  );
  const boxShadow = useTransform(
    progress,
    [center - span, center, center + span],
    [
      "0 20px 40px -30px rgba(8,67,146,0.15)",
      "0 40px 100px -40px rgba(8,67,146,0.35)",
      "0 20px 40px -30px rgba(8,67,146,0.15)",
    ]
  );

  return (
    <div
      data-testid={`feature-card-${index}`}
      className="h-full shrink-0 flex items-center justify-center"
      style={{ width: `100vw` }}
    >
      <motion.div
        style={{ scale, y, rotate, boxShadow }}
        className="mx-5 md:mx-10 w-full max-w-5xl rounded-3xl border border-[#e2e8f0] bg-white p-8 md:p-12 grid lg:grid-cols-5 gap-8 items-center will-change-transform"
      >
        <div className="lg:col-span-3">
          <div className="flex items-center justify-between">
            <div className="font-mono text-xs font-bold text-[#084392] tracking-[0.22em]">
              {feature.tag}
            </div>
            <div className="lg:hidden h-11 w-11 rounded-xl bg-[#084392]/10 text-[#084392] flex items-center justify-center">
              <Icon size={20} />
            </div>
          </div>
          <h3 className="font-heading mt-6 text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02] whitespace-pre-line">
            {feature.title}
          </h3>
          <p className="mt-5 text-[#475569] text-base md:text-lg max-w-xl leading-relaxed">
            {feature.body}
          </p>
          <div className="mt-6 text-xs font-mono text-[#94a3b8]">
            {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="hidden lg:flex h-14 w-14 rounded-2xl bg-[#084392]/10 text-[#084392] items-center justify-center">
            <Icon size={26} />
          </div>
          <FeatureVisual variant={feature.visual} />
        </div>
      </motion.div>
    </div>
  );
}

function FeatureVisual({ variant }) {
  if (variant === "rupee") {
    return (
      <div className="grid grid-cols-3 gap-2">
        {[
          { k: "MAX LOSS", v: "₹2,500", c: "#ef4444" },
          { k: "TARGET", v: "₹6,000", c: "#10b981" },
          { k: "RISK:REWARD", v: "1 : 2.4", c: "#084392" },
        ].map((t, i) => (
          <div key={i} className="rounded-xl border border-[#e2e8f0] p-3">
            <div className="text-[10px] font-mono text-[#475569]">{t.k}</div>
            <div className="font-heading text-xl" style={{ color: t.c }}>
              {t.v}
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (variant === "discipline") {
    return (
      <div className="rounded-xl border border-[#e2e8f0] p-4 bg-[#f8fafc]">
        <div className="flex items-center justify-between text-xs font-mono">
          <span className="text-[#475569]">AUTO-EXECUTION</span>
          <span className="text-[#10b981] font-bold flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-[#10b981] pulse-dot" />
            RUNNING
          </span>
        </div>
        <div className="mt-3 space-y-1.5 font-mono text-xs">
          <div className="flex justify-between">
            <span className="text-[#475569]">09:16:02</span>
            <span className="text-[#0f172a]">✓ Entry confirmed</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#475569]">10:42:18</span>
            <span className="text-[#0f172a]">✓ SL trailed to +₹400</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#475569]">13:04:55</span>
            <span className="text-[#10b981]">✓ Target hit — ₹6,000</span>
          </div>
        </div>
      </div>
    );
  }
  if (variant === "adapt") {
    return (
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-[#e2e8f0] p-3">
          <div className="text-[10px] font-mono text-[#475569]">BEFORE</div>
          <div className="mt-1 text-sm font-semibold">Bullish spread</div>
          <div className="mt-2 flex gap-1">
            <div className="h-2 flex-1 rounded bg-[#10b981]" />
            <div className="h-2 flex-1 rounded bg-[#10b981]/50" />
            <div className="h-2 flex-1 rounded bg-[#e2e8f0]" />
          </div>
        </div>
        <div className="rounded-xl border border-[#084392]/40 p-3 bg-[#084392]/5">
          <div className="text-[10px] font-mono text-[#084392]">AFTER REVERSAL</div>
          <div className="mt-1 text-sm font-semibold">Auto-flipped bearish</div>
          <div className="mt-2 flex gap-1">
            <div className="h-2 flex-1 rounded bg-[#e2e8f0]" />
            <div className="h-2 flex-1 rounded bg-[#ef4444]/50" />
            <div className="h-2 flex-1 rounded bg-[#ef4444]" />
          </div>
        </div>
      </div>
    );
  }
  if (variant === "nocode") {
    return (
      <div className="flex flex-wrap gap-2">
        {["Pick strategy", "Set budget ₹", "Set SL ₹", "Deploy"].map((t, i) => (
          <div
            key={i}
            className="flex items-center gap-2 rounded-full border border-[#e2e8f0] bg-[#f8fafc] px-3 py-1.5 text-sm font-semibold"
          >
            <span className="h-5 w-5 rounded-full bg-[#084392] text-white text-[10px] font-bold flex items-center justify-center">
              {i + 1}
            </span>
            {t}
          </div>
        ))}
      </div>
    );
  }
  return null;
}


/* --------------------------------------------------------
   Thread-wave background — a soft, technical sine wave
   that appears to "carry" the cards. Phase shifts with scroll.
   -------------------------------------------------------- */
function ThreadWave({ progress }) {
  // Shift waves horizontally based on scroll (gives the "carrying" feel)
  const shift = useTransform(progress, [0, 1], [0, -400]);
  const shiftSlow = useTransform(progress, [0, 1], [0, -200]);
  const opacity = useTransform(progress, [0, 0.05, 0.95, 1], [0, 1, 1, 0]);

  return (
    <motion.svg
      aria-hidden
      className="absolute inset-0 w-full h-full pointer-events-none z-0"
      viewBox="0 0 1440 900"
      preserveAspectRatio="none"
      style={{ opacity }}
    >
      <defs>
        <linearGradient id="tc-thread-grad" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#084392" stopOpacity="0" />
          <stop offset="20%" stopColor="#084392" stopOpacity="0.35" />
          <stop offset="50%" stopColor="#084392" stopOpacity="0.55" />
          <stop offset="80%" stopColor="#084392" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#084392" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="tc-thread-grad-soft" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#084392" stopOpacity="0" />
          <stop offset="50%" stopColor="#084392" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#084392" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Back thread (slowest, widest arc) */}
      <motion.g style={{ x: shiftSlow }}>
        <path
          d="M -200 520 Q 160 380, 520 520 T 1240 520 T 1960 520"
          fill="none"
          stroke="url(#tc-thread-grad-soft)"
          strokeWidth="1"
          strokeDasharray="3 6"
          opacity="0.6"
        />
      </motion.g>

      {/* Mid thread */}
      <motion.g style={{ x: shift }}>
        <path
          d="M -200 470 Q 160 340, 520 470 T 1240 470 T 1960 470"
          fill="none"
          stroke="url(#tc-thread-grad)"
          strokeWidth="1.2"
          strokeDasharray="2 10"
        />
        <path
          d="M -200 540 Q 160 680, 520 540 T 1240 540 T 1960 540"
          fill="none"
          stroke="url(#tc-thread-grad)"
          strokeWidth="1"
          strokeDasharray="1 8"
          opacity="0.7"
        />
      </motion.g>

      {/* Front thread (fastest, tightest wave) — this is the "conveyor" */}
      <motion.g style={{ x: shift }}>
        <path
          d="M -200 500 Q 80 430, 360 500 T 920 500 T 1480 500 T 2040 500"
          fill="none"
          stroke="#084392"
          strokeWidth="1.6"
          strokeOpacity="0.55"
          strokeLinecap="round"
        />
        {/* Traveling nodes along the wave */}
        {[0, 320, 640, 960, 1280, 1600].map((cx, i) => (
          <circle
            key={i}
            cx={cx}
            cy={i % 2 === 0 ? 500 : 500}
            r="3"
            fill="#084392"
            opacity="0.8"
          />
        ))}
      </motion.g>

      {/* Static soft horizontal guideline */}
      <line
        x1="0"
        x2="1440"
        y1="500"
        y2="500"
        stroke="#084392"
        strokeOpacity="0.06"
        strokeWidth="1"
      />
    </motion.svg>
  );
}
